/**
 * DirectoryAuditPanel — what the directory is counting, and what the sweep
 * corrected. Provinces, districts, HQ and country offices are groupings: they
 * must stay out of the congregation count and out of the public directory.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, ShieldCheck, Undo2, AlertTriangle } from 'lucide-react';

export const DirectoryAuditPanel = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['church-directory-audit'],
    queryFn: () => api.get('/admin/church-directory-audit').then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['church-directory-audit'] });
    qc.invalidateQueries({ queryKey: ['churches'] });
  };

  const run = useMutation({
    mutationFn: () => api.post('/admin/church-directory-audit/run'),
    onSuccess: (r) => {
      const n = r.data?.corrected || 0;
      toast.success(n ? `Corrected ${n} record${n === 1 ? '' : 's'}` : 'Everything is categorised correctly');
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const revert = useMutation({
    mutationFn: (id) => api.post(`/admin/church-directory-audit/${id}/revert`),
    onSuccess: () => { toast.success('Correction reverted'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <Card data-testid="directory-audit-panel">
        <CardContent className="py-10 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const totals = data?.totals || {};
  const outstanding = data?.outstanding || [];
  const history = data?.history || [];

  return (
    <Card data-testid="directory-audit-panel">
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Directory audit
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Groupings (HQ, country offices, provinces, districts) are held out of the church
            count and the public directory, and their profile URLs list the congregations
            beneath them instead.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => run.mutate()}
          disabled={run.isPending}
          data-testid="run-directory-audit"
        >
          {run.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Re-run sweep
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            ['Congregations', totals.congregations, 'counted in the directory', 'congregations'],
            ['Groupings', totals.groupings_excluded, 'excluded from counts', 'groupings'],
            ['Records', totals.records, 'organisations in total', 'records'],
          ].map(([label, value, sub, key]) => (
            <div key={key} className="rounded-lg border border-slate-200 p-3" data-testid={`audit-total-${key}`}>
              <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
              <p className="text-2xl font-bold text-slate-900">{value ?? '—'}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>
            </div>
          ))}
        </div>

        {data?.last_run?.ran_at && (
          <p className="text-xs text-slate-500" data-testid="audit-last-run">
            Last sweep {new Date(data.last_run.ran_at).toLocaleString()} by {data.last_run.actor || 'system'} ·
            {' '}{data.last_run.corrected || 0} corrected
          </p>
        )}

        {outstanding.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="audit-outstanding">
            <p className="text-sm font-medium flex items-center gap-2 text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              {outstanding.length} record{outstanding.length === 1 ? '' : 's'} still miscategorised
            </p>
            <ul className="mt-2 space-y-1">
              {outstanding.map((c) => (
                <li key={c.church_id} className="text-xs text-amber-900" data-testid={`audit-outstanding-${c.church_id}`}>
                  <span className="font-medium">{c.name}</span>: {c.from_level || 'no level'} → {c.to_level} ({c.reason})
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-slate-800">Corrections made</p>
          {history.length === 0 ? (
            <p className="text-xs text-slate-500 mt-1" data-testid="audit-history-empty">
              No corrections have been needed — every record's level matches its type.
            </p>
          ) : (
            <div className="divide-y mt-2 border rounded-lg">
              {history.map((h) => (
                <div key={h.id} className="p-3 flex items-center gap-3" data-testid={`audit-history-${h.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{h.name}</p>
                    <p className="text-xs text-slate-500">
                      {h.from_level || 'no level'} → {h.to_level} · {h.reason}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {h.applied_at ? new Date(h.applied_at).toLocaleString() : ''} · {h.actor}
                    </p>
                  </div>
                  {h.reverted ? (
                    <Badge variant="outline" className="text-[10px]">Reverted</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => revert.mutate(h.id)}
                      disabled={revert.isPending}
                      data-testid={`revert-correction-${h.id}`}
                    >
                      <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DirectoryAuditPanel;
